import { useCallback, useMemo } from 'react';

import { useSaveSavedSizes } from '@air/feature-saved-sizes/use-save-saved-sizes';
import { useSavedSizesCatalog } from '@air/feature-saved-sizes/use-saved-sizes-catalog';
import { useToasts } from '@air/provider-toast';

import { getMutationErrorMessage } from '~/components/v2/components/saved-settings/utils/getMutationErrorMessage';
import {
  EMPTY_WORKSPACE_AGENT_SMART_RESIZE_SAVED_CATALOG,
  type WorkspaceAgentSmartResizeSavedCatalog,
  type WorkspaceAgentSmartResizeSavedCatalogSaveParams,
  workspaceAgentSmartResizeSavedSizeFromPreset,
  workspaceAgentSmartResizeSaveMembers,
  workspaceAgentSmartResizeSizeSetsFromCatalog,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentSmartResizeSavedCatalog';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { canEditSavedSizes, canViewSavedSizes } from '~/utils/permissions/workspacePermissions';

export const usePrivateWorkspaceAgentSmartResizeSavedCatalog = (): WorkspaceAgentSmartResizeSavedCatalog => {
  const { savedSizesEnabled } = useTemporaryFeatureFlags();
  const { currentWorkspace } = useCurrentWorkspace();
  const { data: permissions, isFetched: arePermissionsFetched } = useCurrentWorkspacePermissionsContext();
  const { showToast } = useToasts();
  const workspaceId = currentWorkspace?.id;
  const canView = savedSizesEnabled && canViewSavedSizes(permissions);
  const canEdit = savedSizesEnabled && canEditSavedSizes(permissions);
  const { isLoading, sizePresets, sizeSets } = useSavedSizesCatalog({
    enabled: canView,
    workspaceId,
  });
  const { saveCustomSize, saveSet } = useSaveSavedSizes({ sizePresets, workspaceId });

  const savedSizes = useMemo(() => sizePresets.map(workspaceAgentSmartResizeSavedSizeFromPreset), [sizePresets]);
  const savedSets = useMemo(
    () => workspaceAgentSmartResizeSizeSetsFromCatalog(sizeSets, sizePresets),
    [sizePresets, sizeSets],
  );

  const onSave = useCallback(
    async ({ name, sizes }: WorkspaceAgentSmartResizeSavedCatalogSaveParams): Promise<boolean> => {
      const [onlySize, ...rest] = sizes;

      try {
        if (onlySize?.by === 'exact-size' && !onlySize.formatId && rest.length === 0) {
          await saveCustomSize({ height: onlySize.height, name, width: onlySize.width });
          showToast(`${name} saved to Context`, { color: 'black' });

          return true;
        }

        const members = workspaceAgentSmartResizeSaveMembers(sizes, sizePresets);

        if (!members) {
          showToast('Only pixel sizes can be saved', { color: 'red' });

          return false;
        }

        const result = await saveSet({ members, name });

        if (result.status === 'error') {
          showToast(getMutationErrorMessage(result.error, 'Failed to create set'), { color: 'red' });

          return false;
        }

        showToast(`${name} saved as a set`, { color: 'black' });

        return true;
      } catch (error) {
        showToast(getMutationErrorMessage(error, 'Failed to add size'), { color: 'red' });

        return false;
      }
    },
    [saveCustomSize, saveSet, showToast, sizePresets],
  );

  if (!savedSizesEnabled) {
    return EMPTY_WORKSPACE_AGENT_SMART_RESIZE_SAVED_CATALOG;
  }

  return {
    isLoading: !arePermissionsFetched || (canView && isLoading),
    onSave: canEdit ? onSave : undefined,
    sets: canView ? savedSets : [],
    sizes: canView ? savedSizes : [],
  };
};
